/**
 * Live countdown for the announcement bar.
 *
 * The deadline is worked out in Liquid, in the SHOP's timezone, and handed over
 * as a Unix timestamp in data-ends. That matters: a shopper in New York and one
 * in London must both count down to the same 4pm UK dispatch cutoff, so the
 * target can't be derived from the visitor's own clock.
 *
 * Notes:
 *  - The markup already contains a server-rendered value, so the bar never
 *    flashes empty before this runs and there is no layout shift.
 *  - Ticking stops while the tab is hidden and resumes on return, so a
 *    background tab isn't waking the main thread once a second forever.
 */
const TIMER_UNITS = [
  { key: 'd', secs: 86400 },
  { key: 'h', secs: 3600 },
  { key: 'm', secs: 60 },
  { key: 's', secs: 1 },
];

const TIMER_WORDS = {
  d: ['day', 'days'],
  h: ['hour', 'hours'],
  m: ['min', 'mins'],
  s: ['sec', 'secs'],
};

class AnnouncementTimer extends HTMLElement {
  connectedCallback() {
    this.endMs = Number(this.dataset.ends) * 1000;
    this.format = this.dataset.format || 'words';
    this.expiredText = this.dataset.expiredText || '';
    this.done = false;

    if (!this.endMs || Number.isNaN(this.endMs)) return;

    this.tick = this.tick.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    this.tick();
    this.start();
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  start() {
    if (this.timer || this.done) return;
    this.timer = setInterval(this.tick, 1000);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  onVisibility() {
    if (document.hidden) {
      this.stop();
    } else {
      // Catch up immediately: the clock moved on while we weren't ticking.
      this.tick();
      this.start();
    }
  }

  tick() {
    let left = Math.floor((this.endMs - Date.now()) / 1000);

    if (left <= 0) {
      this.done = true;
      this.stop();
      this.textContent = this.expiredText;
      // With no replacement copy the whole announcement is pointless, so drop
      // it rather than leaving a sentence with a hole in it.
      if (!this.expiredText) {
        const line = this.closest('.announcement__text');
        if (line) line.hidden = true;
      }
      return;
    }

    const parts = [];
    for (const unit of TIMER_UNITS) {
      const value = Math.floor(left / unit.secs);
      left -= value * unit.secs;
      parts.push({ key: unit.key, value });
    }

    this.textContent = this.render(parts);
  }

  /** @param {{key: string, value: number}[]} parts */
  render(parts) {
    const [d, h, m, s] = parts;

    if (this.format === 'digits') {
      const clock = [h, m, s].map((p) => String(p.value).padStart(2, '0')).join(':');
      return d.value > 0 ? `${d.value}d ${clock}` : clock;
    }

    if (this.format === 'compact') {
      return parts
        .filter((p, i) => p.value > 0 || i > 1)
        .map((p) => `${p.value}${p.key}`)
        .join(' ');
    }

    // Words: drop leading zero units so "0 days 0 hours 9 mins" never appears,
    // but always keep minutes and seconds so the number visibly moves.
    return parts
      .filter((p, i) => p.value > 0 || i > 1)
      .map((p) => `${p.value} ${TIMER_WORDS[p.key][p.value === 1 ? 0 : 1]}`)
      .join(' ');
  }
}

customElements.define('announcement-timer', AnnouncementTimer);
